import type { BuilderApiAdapter, BuilderAsset, PaginatedResult } from '@printtat/builder';
import { apiFetch, apiJson } from '@/lib/apiFetch';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:4000';

/** Shape returned by the backend assets endpoint. `hasTransparency` may be null. */
interface RawAsset {
  id: string;
  name: string;
  url: string;
  originalName: string;
  mimeType: string;
  width: number | null;
  height: number | null;
  hasTransparency: boolean | null;
  source: 'user' | 'printtat';
  uploadedByUserId: string;
  uploadedBy: { id: string; name: string; email: string } | null;
  categoryId: string | null;
  categoryName: string | null;
}

/** Prepend API_URL to relative paths so images resolve to the backend */
const resolveUrl = (url: string): string =>
  url.startsWith('/') ? `${API_URL}${url}` : url;

/** Resolve asset URLs and coerce nullable fields into the BuilderAsset shape */
const normalizeAsset = (raw: RawAsset): BuilderAsset => ({
  id: raw.id,
  name: raw.name,
  url: resolveUrl(raw.url),
  originalName: raw.originalName,
  mimeType: raw.mimeType,
  width: raw.width,
  height: raw.height,
  hasTransparency: raw.hasTransparency,
  source: raw.source,
  uploadedByUserId: raw.uploadedByUserId,
  uploadedBy: raw.uploadedBy,
  categoryId: raw.categoryId,
  categoryName: raw.categoryName,
});

export const adminBuilderApi: BuilderApiAdapter = {
  getDesign(id) {
    return apiJson(`/manage/template-designs/${id}`);
  },

  createDesign(data) {
    return apiJson('/manage/template-designs', { method: 'POST', body: data });
  },

  async updateDesign(id, data) {
    await apiJson(`/manage/template-designs/${id}`, { method: 'PUT', body: data });
  },

  uploadSurfaceExport(file, designId) {
    const form = new FormData();
    form.append('file', file);
    form.append('designId', designId);
    return apiJson('/manage/template-designs/upload-surface-export', { method: 'POST', body: form });
  },

  getProduct(id) {
    return apiJson(`/products/${id}`);
  },

  async listAssets(params) {
    const qs = new URLSearchParams();
    if (params.source) qs.set('source', params.source);
    if (params.category) qs.set('category', params.category);
    if (params.search) qs.set('search', params.search);
    if (params.page) qs.set('page', String(params.page));
    if (params.limit) qs.set('limit', String(params.limit));
    const json = await apiJson<PaginatedResult<RawAsset>>(`/assets?${qs}`);
    return {
      ...json,
      data: (json.data ?? []).map(normalizeAsset),
    };
  },

  listAssetCategories() {
    return apiJson('/asset-categories');
  },

  async uploadAsset(file, meta) {
    const form = new FormData();
    form.append('file', file);
    form.append('width', String(meta.width));
    form.append('height', String(meta.height));
    return normalizeAsset(await apiJson<RawAsset>('/manage/assets/upload', { method: 'POST', body: form }));
  },

  async deleteAsset(id, force) {
    const res = await apiFetch(`/assets/${id}${force ? '?force=true' : ''}`, { method: 'DELETE' });
    if (res.status === 409) {
      return res.json();
    }
    if (!res.ok) throw new Error('Failed to delete asset');
    return { conflict: false };
  },

  async renameAsset(id, name) {
    return normalizeAsset(await apiJson<RawAsset>(`/assets/${id}`, { method: 'PATCH', body: { name } }));
  },
};
